import type { AnalyticsFilter } from '@ics-referral-hub/shared-types';

export type AnalyticsDatePreset = '7d' | '30d' | '90d' | 'custom';

export function presetToRange(preset: '7d' | '30d' | '90d'): { start: Date; end: Date } {
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  const days = preset === '7d' ? 7 : preset === '90d' ? 90 : 30;
  const start = new Date(end);
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - (days - 1));
  return { start, end };
}

export function defaultAnalyticsFilter(): AnalyticsFilter {
  const { start, end } = presetToRange('30d');
  return {
    startDate: start.toISOString(),
    endDate: end.toISOString(),
  };
}

export function startOfDay(d: Date): Date {
  const copy = new Date(d);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

export function endOfDay(d: Date): Date {
  const copy = new Date(d);
  copy.setHours(23, 59, 59, 999);
  return copy;
}

/** UTC date key YYYY-MM-DD (matches Mongo $dateToString on referredDate). */
export function toUtcDateKey(epochMs: number): string {
  return new Date(epochMs).toISOString().slice(0, 10);
}

export interface TimeSeriesPointLike {
  date: string;
  count: number;
}

export type VolumeSeriesGranularity = 'day' | 'week';

export interface NormalizedVolumeSeries {
  points: TimeSeriesPointLike[];
  granularity: VolumeSeriesGranularity;
}

const MS_PER_DAY = 86_400_000;
const WEEKLY_THRESHOLD_DAYS = 120;

function utcDayStart(iso: string): number {
  const d = new Date(iso);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

function buildCountMap(points: TimeSeriesPointLike[]): Map<string, number> {
  const byDate = new Map<string, number>();
  for (const p of points) {
    const key = p.date.slice(0, 10);
    byDate.set(key, (byDate.get(key) ?? 0) + p.count);
  }
  return byDate;
}

function fillUtcDays(byDate: Map<string, number>, utcStart: number, utcEnd: number): TimeSeriesPointLike[] {
  const result: TimeSeriesPointLike[] = [];
  for (let t = utcStart; t <= utcEnd; t += MS_PER_DAY) {
    const key = toUtcDateKey(t);
    result.push({ date: key, count: byDate.get(key) ?? 0 });
  }
  return result;
}

/** Monday-based week start in UTC. */
function utcWeekStart(epochMs: number): number {
  const d = new Date(epochMs);
  const day = d.getUTCDay();
  const diff = day === 0 ? -6 : 1 - day;
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + diff);
}

function fillUtcWeeks(byDate: Map<string, number>, utcStart: number, utcEnd: number): TimeSeriesPointLike[] {
  const weekTotals = new Map<string, number>();

  for (const [dateKey, count] of byDate) {
    const weekKey = toUtcDateKey(utcWeekStart(Date.parse(`${dateKey}T12:00:00Z`)));
    weekTotals.set(weekKey, (weekTotals.get(weekKey) ?? 0) + count);
  }

  const result: TimeSeriesPointLike[] = [];
  let week = utcWeekStart(utcStart);
  const lastWeek = utcWeekStart(utcEnd);

  while (week <= lastWeek) {
    const key = toUtcDateKey(week);
    result.push({ date: key, count: weekTotals.get(key) ?? 0 });
    week += 7 * MS_PER_DAY;
  }

  return result;
}

/**
 * Expand API volume points across the full filter range (zeros included).
 * Uses UTC keys to match the API. Switches to weekly buckets for long ranges.
 */
export function normalizeVolumeSeries(
  points: TimeSeriesPointLike[],
  startIso?: string,
  endIso?: string
): NormalizedVolumeSeries {
  if (!startIso || !endIso) {
    return { points, granularity: 'day' };
  }

  const utcStart = utcDayStart(startIso);
  const utcEnd = utcDayStart(endIso);
  if (utcEnd < utcStart) {
    return { points: [], granularity: 'day' };
  }

  const dayCount = Math.floor((utcEnd - utcStart) / MS_PER_DAY) + 1;
  const byDate = buildCountMap(points);

  if (dayCount > WEEKLY_THRESHOLD_DAYS) {
    return {
      points: fillUtcWeeks(byDate, utcStart, utcEnd),
      granularity: 'week',
    };
  }

  return {
    points: fillUtcDays(byDate, utcStart, utcEnd),
    granularity: 'day',
  };
}

/** @deprecated Use normalizeVolumeSeries */
export function fillDaySeries(
  points: TimeSeriesPointLike[],
  startIso?: string,
  endIso?: string
): TimeSeriesPointLike[] {
  return normalizeVolumeSeries(points, startIso, endIso).points;
}

export function chartScaleMax(values: number[], paddingRatio = 0.12): number {
  const max = values.length > 0 ? Math.max(...values) : 0;
  if (max <= 0) return 1;
  return Math.ceil(max * (1 + paddingRatio));
}
